/** @jsxImportSource @emotion/react */
import {
  Input,
  Typography,
  UtilityButton,
  useForm,
  useTheme,
} from '@quickbit/qb-design-system';
import { useUpdateEffect } from 'react-use';
import { SettlementReportAdjustment } from 'tabs/Accounting/types';
interface Props {
  adjustments: SettlementReportAdjustment[];
  disabled?: boolean;
  onAddAdjustment: () => void;
  addAdjustementLoading: boolean;
  onUpdateAdjustment: (adjustment: SettlementReportAdjustment) => void;
  onDeleteAdjustment: (adjustmentId: string) => void;
  deleteAdjustmentLoading: boolean;
  refreshLoading: boolean;
  maxRefundDebtAdjustment: number | null;
  refundDebtAdjustment: number | null;
  refundDebtAdjustmentSetter: (value: number | null) => void;
  onUpdateRefundDebtAdjustment: () => void;
}

export const Adjustments = ({
  adjustments,
  disabled,
  onAddAdjustment,
  addAdjustementLoading,
  onUpdateAdjustment,
  onDeleteAdjustment,
  deleteAdjustmentLoading,
  refreshLoading,
  maxRefundDebtAdjustment,
  refundDebtAdjustment,
  refundDebtAdjustmentSetter,
  onUpdateRefundDebtAdjustment,
}: Props) => {
  const { spacing } = useTheme();
  const { fields, setters, errors } = useForm(adjustments, {
    label: (v) => (v === '' ? 'Cannot be empty' : undefined),
    amount: (v: number | string | null) =>
      v == null || isNaN(v as number) || v === ''
        ? 'Not a valid number.'
        : undefined,
  });

  useUpdateEffect(() => {
    setters.set(adjustments);
  }, [setters, adjustments]);

  console.log(
    `Adjustments.tsx, refundDebtAdjustment ${refundDebtAdjustment}, maxRefundDebtAdjustment ${maxRefundDebtAdjustment}`
  );

  return (
    <>
      {(!disabled || !!fields.length) && (
        <Typography variant="subtitle">Adjustments</Typography>
      )}
      {refundDebtAdjustment != null && maxRefundDebtAdjustment && (
        <div css={{ display: 'flex', gap: spacing.xxs }}>
          <Input value={'Refund debt'} disabled={true} />
          <Input
            type="number"
            label={'Amount'}
            value={refundDebtAdjustment.toString()}
            helperText={
              refundDebtAdjustment < maxRefundDebtAdjustment ||
              refundDebtAdjustment > 0
                ? `Adjustment for refunds must be betweeen ${maxRefundDebtAdjustment} and 0`
                : ''
            }
            onBlur={() => {
              onUpdateRefundDebtAdjustment();
            }}
            onChange={(e) => {
              refundDebtAdjustmentSetter(
                e.target.value
                  ? parseFloat(parseFloat(e.target.value).toFixed(2))
                  : ('' as unknown as number)
              );
            }}
          />
        </div>
      )}
      {fields.map((adjustment, idx) => (
        <div
          key={adjustment.uuid}
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: spacing.xxs,
          }}
        >
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <Input
              label={'Label'}
              value={adjustment.label}
              onBlur={() => {
                const inputError = !!errors[idx]?.label;
                if (!inputError) {
                  onUpdateAdjustment(adjustment);
                }
              }}
              onChange={(e) => {
                setters.list[idx].label.set(e.target.value);
              }}
              error={!!errors[idx]?.label}
              helperText={errors[idx]?.label}
              disabled={disabled}
            />
            <Input
              type="number"
              label={'Amount'}
              value={adjustment.amount.toString()}
              onBlur={() => {
                onUpdateAdjustment(adjustment);
              }}
              onChange={(e) => {
                setters.list[idx].amount.set(
                  e.target.value
                    ? parseFloat(parseFloat(e.target.value).toFixed(2))
                    : ('' as unknown as number)
                );
              }}
              error={!!errors[idx]?.amount}
              helperText={errors[idx]?.amount}
              disabled={disabled}
            />
          </div>
          {!disabled && (
            <div css={{ display: 'flex', alignItems: 'center' }}>
              <UtilityButton
                icon="trash"
                variant="icon"
                onClick={() => onDeleteAdjustment(adjustment.uuid)}
                loading={deleteAdjustmentLoading || refreshLoading}
                disabled={deleteAdjustmentLoading || refreshLoading}
              />
            </div>
          )}
        </div>
      ))}
      {!disabled && (
        <div css={{ display: 'flex', justifyContent: 'start' }}>
          <UtilityButton
            icon="plus"
            text="Add adjustment"
            onClick={onAddAdjustment}
            disabled={addAdjustementLoading || refreshLoading}
          />
        </div>
      )}
    </>
  );
};
